import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseUrls } from '@app/app.constants';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { Holiday } from '@app/core/interfaces/c5/holiday';
import { HolidayList } from '@app/core/interfaces/c5/holiday-list';
import { UserLevel } from '@app/core/models/user-level';
import { Utils } from '@app/shared/services/utils.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HolidayListRepository {
  constructor(
    private authenticationService: AuthenticationService,
    private http: HttpClient,
    private translateService: TranslateService,
    private utils: Utils
  ) {}

  addHoliday(holiday: Holiday) {
    return this.utils
      .apiUrl(`/holidayLists/${holiday.holidayListId}/day`)
      .pipe(mergeMap((url) => this.http.post<void>(url, holiday)));
  }

  addHolidayList(holidayList: Partial<HolidayList>) {
    return this.utils.apiUrl(`/holidayLists`).pipe(mergeMap((url) => this.http.post<void>(url, holidayList)));
  }

  deleteHoliday(holidayListId: number, id: number): Observable<void> {
    return this.utils
      .apiUrl(`/holidayLists/${holidayListId}/day/${id}`)
      .pipe(mergeMap((url) => this.http.delete<void>(url)));
  }

  deleteHolidayList(id: number): Observable<void> {
    return this.utils.apiUrl(`/holidayLists/${id}`).pipe(mergeMap((url) => this.http.delete<void>(url)));
  }

  getHolidayList(id: number): Observable<HolidayList> {
    return this.utils.apiUrl(`/holidayLists/${id}`).pipe(mergeMap((url) => this.http.get<HolidayList>(url)));
  }

  list(): Observable<HolidayList[]> {
    let response$: Observable<HolidayList[]>;

    if (this.authenticationService.userIsEndUser() === true) {
      response$ = of([]);
    } else {
      const url = {
        [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/holidayLists',
        [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + '/holidayLists',
        [UserLevel.EndUser]: BaseUrls.Enterprise + '/holidayLists',
      };

      response$ = this.utils.apiUrl(url).pipe(mergeMap((url) => this.http.get<HolidayList[]>(url)));
    }

    return response$;
  }

  listParentHolidayLists(): Observable<HolidayList[]> {
    const url = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/parentHolidayLists',
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + '/parentHolidayLists',
      [UserLevel.EndUser]: BaseUrls.EndUser + '/parentHolidayLists',
    };

    return this.utils.apiUrl(url).pipe(mergeMap((url) => this.http.get<HolidayList[]>(url)));
  }

  updateHoliday(holiday: Holiday): Observable<void> {
    return this.utils
      .apiUrl(`/holidayLists/${holiday.holidayListId}/day/${holiday.id}`)
      .pipe(mergeMap((url) => this.http.put<void>(url, holiday)));
  }

  updateHolidayList(holidayLists: HolidayList): Observable<void> {
    return this.utils
      .apiUrl(`/holidayLists/${holidayLists.id}`)
      .pipe(mergeMap((url) => this.http.put<void>(url, holidayLists)));
  }
}
